import React, { useEffect, useState } from "react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { getMsalConfig, loadAuthConfig, resetAuthConfig } from "../config/authConfig";
import { AUTH_CONTROLE_MISLUKT, OPNIEUW_PROBEREN } from "../utils/authMessages";
import Spinner from "./common/Spinner";
import Alert from "./common/Alert";

export let msalInstance = null;

// Module-level singleton: onder StrictMode draait het mount-effect twee keer.
// Zonder deze dedup werden er twee PublicClientApplication-instances gebouwd en
// draaide handleRedirectPromise() twee keer, waarbij de tweede pass de redirect
// kon opeten die de eerste nodig had.
let bootstrapPromise = null;

function bootstrapMsal() {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = (async () => {
    // 1) Load backend public config
    await loadAuthConfig();

    // 2) Create MSAL instance with resolved config and initialize
    const inst = new PublicClientApplication(getMsalConfig());
    await inst.initialize();

    // Event callback wordt één keer voor de app-levensduur geregistreerd, zodat
    // de cleanup van een afgebroken mount de overlevende instance niet afmeldt.
    inst.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
        event.eventType === EventType.SSO_SILENT_SUCCESS
      ) {
        const account = event.payload && event.payload.account;
        if (account) {
          inst.setActiveAccount(account);
        }
      }
    });

    // Verwerk redirect response en stel active account in (exact één keer)
    const response = await inst.handleRedirectPromise();
    if (response?.account) {
      inst.setActiveAccount(response.account);
    }
    if (!inst.getActiveAccount()) {
      const all = inst.getAllAccounts();
      if (all.length > 0) {
        inst.setActiveAccount(all[0]);
      }
    }
    msalInstance = inst;
    return inst;
  })();
  // Sta een expliciete retry toe na een mislukte bootstrap.
  bootstrapPromise.catch(() => {
    bootstrapPromise = null;
  });
  return bootstrapPromise;
}

const MsalProviderWrapper = ({ children }) => {
  const [state, setState] = useState({ ready: false, error: null });

  useEffect(() => {
    let mounted = true;
    // `mounted` bewaakt alleen setState, niet het async werk zelf: de bootstrap
    // moet doorlopen ook als deze mount wordt opgeruimd.
    bootstrapMsal()
      .then(() => {
        if (mounted) setState({ ready: true, error: null });
      })
      .catch((err) => {
        if (mounted) setState({ ready: true, error: err });
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!state.ready) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-busy="true"
        style={{
          position: "fixed",
          inset: 0,
          background: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        <Spinner size={28} />
        <div style={{ marginTop: 12, color: "var(--color-text-600)" }}>App wordt geladen…</div>
      </div>
    );
  }

  if (state.error || !msalInstance) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          padding: "24px",
          zIndex: 9999,
        }}
      >
        <Alert type="error" style={{ maxWidth: "440px" }}>
          {AUTH_CONTROLE_MISLUKT}
        </Alert>
        <button
          type="button"
          className="btn"
          onClick={() => {
            resetAuthConfig();
            bootstrapPromise = null;
            window.location.reload();
          }}
        >
          {OPNIEUW_PROBEREN}
        </button>
      </div>
    );
  }
  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
};

export default MsalProviderWrapper;
